import format from 'date-fns/format';
import parseISO from 'date-fns/parseISO';
import {
	TDeliveryException,
	TDeliveryExceptionCategory,
	TDeliveryExceptionMetric,
	TExceptionView,
	TFutileType,
	TSifotException,
} from '@types';
import { deltaDirection, weekLabel } from './utilisationMetrics';
import type { TCellTone } from './utilisationMetrics';
import { csvCell } from './csvUtils';

export const ALL_CATEGORIES = 'All categories';

export type TExceptionCategoryDefinition = { value: TDeliveryExceptionCategory; label: string; info: string };

/** In the order the grid lists them. */
export const DELIVERY_EXCEPTION_CATEGORIES: TExceptionCategoryDefinition[] = [
	{
		value: 'demurrage', label: 'Demurrage', info: 'On time: waiting time at a site beyond the free period, charged per hour.'
	},
	{
		value: 'futile', label: 'Futile', info: 'Truck attended but could not load or unload. Charged either as a Futile I&C claim, or as a cancelled shipment with part of its cost still paid.'
	},
	{
		value: 're_delivery', label: 'Re-delivery', info: 'A second delivery attempt after the first was refused or missed.'
	},
	{
		value: 'returns', label: 'Returns', info: 'Freight sent back to origin after delivery was rejected.'
	},
	{
		value: 'storage', label: 'Storage', info: 'Freight held by the carrier awaiting a delivery slot, charged per day.'
	},
];

export const SIFOT_CATEGORIES: TExceptionCategoryDefinition[] = [
	{
		value: 'qty_difference', label: 'QTY difference', info: 'The POD shows fewer pallets or spaces than were booked, as read by the POD AI review.'
	},
];

export const DELIVERY_EXCEPTION_CATEGORY_LABELS = Object.fromEntries(
	[...DELIVERY_EXCEPTION_CATEGORIES, ...SIFOT_CATEGORIES].map((category) => [category.value, category.label])
) as Record<TDeliveryExceptionCategory, string>;

const CLOSED_WEEKS = 'Only closed billing weeks (Monday to Sunday) are shown.';

/** What each view lists, which metrics it offers, and how its figures are worked out. */
export const EXCEPTION_VIEWS: Record<TExceptionView, {
	label: string;
	categories: TExceptionCategoryDefinition[];
	metrics: TDeliveryExceptionMetric[];
	methodology: string;
}> = {
	delivery_exceptions: {
		label: 'Delivery exceptions',
		categories: DELIVERY_EXCEPTION_CATEGORIES,
		metrics: ['amount', 'occurrences', 'chargeable_hours'],
		methodology: 'Charges outside the rate card, by the week they were approved. A cancelled booking counts in the week it was cancelled. '
			+ 'Figures are net of credits; a charge reversed in the same week is not shown. '
			+ `Chargeable hours are recorded from 25 June 2026; earlier demurrage shows its cost without hours. ${CLOSED_WEEKS}`,
	},
	sifot: {
		label: 'SIFOT',
		categories: SIFOT_CATEGORIES,
		metrics: ['occurrences'],
		methodology: 'Deliveries where the POD shows fewer pallets or spaces than were booked, by the week they were delivered. '
			+ `Read by the POD AI review from 26 October 2026. ${CLOSED_WEEKS}`,
	},
};

export const FUTILE_TYPE_LABELS: Record<TFutileType, string> = {
	pick_up: 'Pick-up',
	drop_off: 'Drop-off',
	cancelled_booking: 'Cancelled booking',
};

export const DELIVERY_EXCEPTION_METRIC_LABELS: Record<TDeliveryExceptionMetric, string> = {
	amount: 'Cost',
	occurrences: 'Occurrences',
	chargeable_hours: 'Chargeable hours',
};

export type TDeliveryExceptionTotals = {
	amount: number;
	/** Charges and short deliveries, not credits: a refund is not another exception. */
	occurrences: number;
	/** Demurrage lines in the group, so a group without any shows no hours rather than 0. */
	demurrageLines: number;
	/** Net of credits, which carry the minutes they refund as a negative. */
	chargeableMinutes: number;
	/** Demurrage lines with no minutes to go by, which are missing data, not free waits. */
	missingMinutes: number;
}

export const exceptionTotals = (charges: TDeliveryException[]): TDeliveryExceptionTotals => {
	const totals = charges.reduce<TDeliveryExceptionTotals>((sum, charge) => {
		const isOccurrence = charge.source === 'pod' || charge.amount > 0;
		const isDemurrage = charge.category === 'demurrage';

		return {
			amount: sum.amount + charge.amount,
			occurrences: sum.occurrences + (isOccurrence ? 1 : 0),
			demurrageLines: sum.demurrageLines + (isDemurrage ? 1 : 0),
			chargeableMinutes: sum.chargeableMinutes + (isDemurrage ? charge.chargeable_minutes ?? 0 : 0),
			missingMinutes: sum.missingMinutes + (isDemurrage && charge.chargeable_minutes === null ? 1 : 0),
		};
	}, {
		amount: 0, occurrences: 0, demurrageLines: 0, chargeableMinutes: 0, missingMinutes: 0
	});

	// Summed in cents so a column of dollars and cents does not drift.
	return { ...totals, amount: Math.round(totals.amount * 100) / 100 };
}

/** Week => totals for the charges in it; null for a week with none. */
export const totalsByWeek = (
	charges: TDeliveryException[],
	weeks: string[],
): Record<string, TDeliveryExceptionTotals | null> => Object.fromEntries(weeks.map((week) => {
	const inWeek = charges.filter((charge) => charge.week === week);
	return [week, inWeek.length === 0 ? null : exceptionTotals(inWeek)];
}));

/**
 * The figure a cell shows. Hours exist for demurrage alone, so any other group
 * has none to show rather than a zero.
 */
export const exceptionCellValue = (
	totals: TDeliveryExceptionTotals | null,
	metric: TDeliveryExceptionMetric,
): number | null => {
	if (totals === null) return null;
	if (metric === 'amount') return totals.amount;
	if (metric === 'occurrences') return totals.occurrences;
	if (totals.demurrageLines === 0 || totals.demurrageLines === totals.missingMinutes) return null;
	return totals.chargeableMinutes / 60;
}

/** Favourable when a week costs or happens less than the one before, unfavourable when more. Lower is always better. */
export const exceptionTone = (value: number | null, previous: number | null): TCellTone => {
	const direction = deltaDirection(value, previous);
	if (direction === null) return null;
	return direction === 'down' ? 'favourable' : 'unfavourable';
}

/** Some demurrage in the group has no minutes to go by, so its hours are incomplete. */
export const isPartialHours = (totals: TDeliveryExceptionTotals | null, metric: TDeliveryExceptionMetric): boolean =>
	metric === 'chargeable_hours' && !!totals && totals.missingMinutes > 0 && totals.missingMinutes < totals.demurrageLines;

/**
 * A week cell's direction against the week before it. Only two weeks that
 * both have a figure compare, as the design shows: the first week has nothing
 * before it, an empty week has no figure, and a partial-hours figure is
 * incomplete, so not comparable with an exact one either way.
 */
export const weekTone = (
	byWeek: Record<string, TDeliveryExceptionTotals | null>,
	weeks: string[],
	week: string,
	metric: TDeliveryExceptionMetric,
): TCellTone => {
	const index = weeks.indexOf(week);
	if (index <= 0) return null;

	const totals = byWeek[week] ?? null;
	const previous = byWeek[weeks[index - 1]] ?? null;
	if (isPartialHours(totals, metric) || isPartialHours(previous, metric)) return null;

	return exceptionTone(exceptionCellValue(totals, metric), exceptionCellValue(previous, metric));
}

/** One grid row: All categories, or one category, with its totals by week and overall. */
export type TDeliveryExceptionRow = {
	category: TDeliveryExceptionCategory | null;
	label: string;
	info: string | null;
	byWeek: Record<string, TDeliveryExceptionTotals | null>;
	total: TDeliveryExceptionTotals | null;
}

/**
 * The grid's rows: All categories over every charge given, then one row per
 * selected category in the view's fixed order, kept even when empty so the
 * table reads the same week to week.
 */
export const deliveryExceptionRows = (
	charges: TDeliveryException[],
	weeks: string[],
	categories: TDeliveryExceptionCategory[],
	definitions: TExceptionCategoryDefinition[] = DELIVERY_EXCEPTION_CATEGORIES,
): TDeliveryExceptionRow[] => {
	const row = (category: TDeliveryExceptionCategory | null, label: string, info: string | null): TDeliveryExceptionRow => {
		const inRow = category === null ? charges : charges.filter((charge) => charge.category === category);
		return {
			category, label, info, byWeek: totalsByWeek(inRow, weeks), total: inRow.length ? exceptionTotals(inRow) : null
		};
	};

	return [
		row(null, ALL_CATEGORIES, null),
		...definitions
			.filter((category) => categories.includes(category.value))
			.map((category) => row(category.value, category.label, category.info)),
	];
}

const DASH = '–';

export const formatMoney = (value: number): string =>
	`${value < 0 ? '-' : ''}$${Math.abs(value).toLocaleString('en-AU', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

export const formatExceptionValue = (value: number | null, metric: TDeliveryExceptionMetric): string => {
	if (value === null) return DASH;
	if (metric === 'amount') return formatMoney(value);
	if (metric === 'occurrences') return Math.round(value).toLocaleString('en-AU');
	return `${value.toLocaleString('en-AU', { maximumFractionDigits: 1 })} h`;
}

export const formatChargeableHours = (minutes: number | null): string =>
	minutes === null ? DASH : `${(minutes / 60).toLocaleString('en-AU', { maximumFractionDigits: 2 })} h`;

/**
 * The date part as the API sent it, in Sydney time. Parsing the full timestamp
 * would shift it into the viewer's zone, so a Monday 00:30 charge read in Perth
 * would show a date outside the week column it sits under.
 */
export const formatChargeDate = (value: string): string => {
	const parsed = parseISO(value.slice(0, 10));
	return isNaN(parsed.valueOf()) ? value : format(parsed, 'dd/MM/yyyy');
}

/** `dd/MM/yyyy HH:mm` read straight off the API's Sydney wall time, for the same reason as above. */
export const formatWallDateTime = (value: string | null): string => {
	const match = value?.match(/^(\d{4})-(\d{2})-(\d{2})[ T](\d{2}):(\d{2})/);
	return match ? `${match[3]}/${match[2]}/${match[1]} ${match[4]}:${match[5]}` : (value ?? DASH);
}

/** A SIFOT row from the API in the shape the grid totals: no dollars, hours or reason. */
export const sifotExceptions = (rows: TSifotException[]): TDeliveryException[] => rows.map((row) => ({
	...row,
	amount: 0,
	chargeable_minutes: null,
	reason: null,
	futile_type: null,
	location_role: 'dropoff',
}));

/** The end of the lane the charge was raised at. */
export const chargeSite = (charge: TDeliveryException): { at: string; site: string; state: string } => {
	const pickup = charge.location_role === 'pickup';
	const name = pickup
		? charge.pickup_location_name ?? charge.pickup_company ?? charge.pickup_suburb
		: charge.dropoff_location_name ?? charge.dropoff_company ?? charge.dropoff_suburb;

	return {
		at: pickup ? 'Pick-up' : 'Drop-off',
		site: name ?? DASH,
		state: (pickup ? charge.pickup_state : charge.dropoff_state) ?? DASH,
	};
}

/** One line of a category's lane and location breakdown, with the rows behind it. */
export type TLaneLocationRow = {
	key: string;
	lane: string;
	location: string;
	byWeek: Record<string, TDeliveryExceptionTotals | null>;
	total: TDeliveryExceptionTotals;
	charges: TDeliveryException[];
}

/**
 * The rows grouped by lane and the site they were raised at, largest first on
 * the metric shown, so the lanes behind most of a category's figure lead.
 */
export const laneLocationRows = (
	charges: TDeliveryException[],
	weeks: string[],
	metric: TDeliveryExceptionMetric,
): TLaneLocationRow[] => {
	const groups = new Map<string, TDeliveryException[]>();
	charges.forEach((charge) => {
		const key = `${charge.lane}|${chargeSite(charge).site}`;
		groups.set(key, [...(groups.get(key) ?? []), charge]);
	});

	const rank = (row: TLaneLocationRow): number => exceptionCellValue(row.total, metric) ?? Number.NEGATIVE_INFINITY;

	return Array.from(groups, ([key, inGroup]) => ({
		key,
		lane: inGroup[0].lane,
		location: chargeSite(inGroup[0]).site,
		byWeek: totalsByWeek(inGroup, weeks),
		total: exceptionTotals(inGroup),
		charges: inGroup,
	})).sort((a, b) => rank(b) - rank(a) || a.key.localeCompare(b.key));
}

/** Futile type is for Ofload staff, so it only comes back from the API to an admin. */
export const hasFutileTypes = (charges: TDeliveryException[]): boolean => charges.some((charge) => charge.futile_type !== null);

/** The charges as CSV, one row each, in the order given. */
export const deliveryExceptionsCsv = (charges: TDeliveryException[]): string => {
	const withFutileType = hasFutileTypes(charges);
	const header = [
		'Date', 'Week', 'Category', ...(withFutileType ? ['Futile type'] : []), 'Shipment Reference', 'Shipment Lane',
		'At', 'Location', 'State', 'Temperature', 'Reason', 'Chargeable hours', 'Cost',
	];

	const lines = charges.map((charge) => {
		const { at, site, state } = chargeSite(charge);

		return [
			formatChargeDate(charge.event_date),
			weekLabel(charge.week),
			DELIVERY_EXCEPTION_CATEGORY_LABELS[charge.category] ?? charge.category,
			...(withFutileType ? [charge.futile_type ? FUTILE_TYPE_LABELS[charge.futile_type] : ''] : []),
			charge.reference,
			charge.lane,
			at,
			site,
			state,
			charge.temperature,
			charge.reason ?? '',
			// A bare number, so a spreadsheet can sum it; the header carries the unit.
			charge.category === 'demurrage' && charge.chargeable_minutes !== null ? String(Math.round(charge.chargeable_minutes / 60 * 100) / 100) : '',
		].map(csvCell).concat(charge.amount.toFixed(2)).join(',');
	});

	return [header.map(csvCell).join(','), ...lines].join('\n');
}

/** The SIFOT deliveries as CSV, one row each, in the order given. */
export const sifotCsv = (deliveries: TDeliveryException[]): string => {
	const header = [
		'Shipment Reference', 'Exception Date', 'Week', 'Category', 'Max. Requested Drop-off', 'Shipment Lane',
		'Booked Truck Size', 'Pick-up Location', 'Pick-up State', 'Drop-off Location', 'Drop-off State',
		'Freight Mode', 'Temperature', 'Unit', 'Booked QTY', 'POD QTY extracted', 'Difference', 'Confidence',
	];

	const lines = deliveries.map((delivery) => [
		delivery.reference,
		formatWallDateTime(delivery.event_date),
		weekLabel(delivery.week),
		DELIVERY_EXCEPTION_CATEGORY_LABELS[delivery.category] ?? delivery.category,
		delivery.requested_drop_off ? formatWallDateTime(delivery.requested_drop_off) : '',
		delivery.lane,
		delivery.truck ?? '',
		delivery.pickup_location_name ?? delivery.pickup_company ?? '',
		delivery.pickup_state ?? '',
		delivery.dropoff_location_name ?? delivery.dropoff_company ?? '',
		delivery.dropoff_state ?? '',
		delivery.freight_mode ?? '',
		delivery.temperature,
		delivery.quantity_unit ?? '',
		String(delivery.quantity_booked ?? ''),
		String(delivery.quantity_found ?? ''),
		String(delivery.quantity_delta ?? ''),
		delivery.quantity_confidence === null || delivery.quantity_confidence === undefined ? '' : delivery.quantity_confidence.toFixed(2),
	].map(csvCell).join(','));

	return [header.map(csvCell).join(','), ...lines].join('\n');
}
